import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, Check, ArrowRight, ArrowLeft, Workflow, Cpu, BarChart3, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const AIAutomationService = () => {
  // Meta tags SEO
  useMetaTags({
    title: 'Solutions IA & Automatisations n8n en Ardennes | HTAG Facility',
    description: 'Solutions IA avancées et automatisations n8n pour PME, associations. Gain de productivité, réduction coûts. Basé à Château-Porcien, Ardennes. Assistance numérique intégrée.',
    keywords: 'IA, intelligence artificielle, n8n, automatisations, workflows, GPT, machine learning, Ardennes, Château-Porcien',
    url: 'https://htagfacility.fr/services/ia-automatisations',
    image: 'https://htagfacility.fr/og-service-ia.png',
    type: 'article'
  });

  // Structured Data - Service
  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Solutions IA & Automatisations n8n',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'HTAG Facility',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Château-Porcien',
        'addressLocality': 'Château-Porcien',
        'addressRegion': 'Ardennes',
        'postalCode': '08320',
        'addressCountry': 'FR'
      }
    },
    'description': 'Intégration IA, automatisations n8n, workflows intelligents pour optimiser processus métier',
    'areaServed': ['FR-08', 'Ardennes', 'Grand Est'],
    'offers': {
      '@type': 'Offer',
      'description': 'Service d\'implémentation IA et automatisations n8n'
    }
  });
  
  const aiFeaturesPhases = [
    {
      title: 'Phase 1: Diagnostic IA',
      icon: Brain,
      description: 'Analyse complète de vos processus pour identifier les opportunités d\'IA. Évaluation de la maturité digitale et des données disponibles.',
      details: ['Audit des processus', 'Identification d\'opportunités IA', 'Évaluation données', 'Faisabilité technique']
    },
    {
      title: 'Phase 2: Conception Intelligente',
      icon: Cpu,
      description: 'Conception d\'architectures IA personnalisées. Sélection des modèles (GPT-4, Claude, LLMs) adaptés à vos besoins spécifiques.',
      details: ['Choix des modèles IA', 'Architecture solution', 'Intégrations APIs', 'Plan de déploiement']
    },
    {
      title: 'Phase 3: Implémentation & Formation',
      icon: Zap,
      description: 'Mise en production des solutions IA, intégration avec vos systèmes. Formation des équipes à l\'utilisation et la gestion des outils IA.',
      details: ['Déploiement en production', 'Configuration modèles', 'Formation équipes', 'Support technique']
    }
  ];

  const automationFeatures = [
    { feature: 'Workflows sans code', desc: 'Créez des workflows complexes avec l\'interface visuelle n8n sans écrire une ligne de code' },
    { feature: '500+ Intégrations', desc: 'Connectez vos outils : CRM, email, cloud, API... Automatisez les échanges de données' },
    { feature: 'AI-Powered Nodes', desc: 'Intégrez IA dans vos workflows : traitement texte, images, transcriptions, prédictions' },
    { feature: 'Monitoring & Alertes', desc: 'Suivi en temps réel des workflows, alertes sur erreurs, logs détaillés' },
    { feature: '24/7 Autonomie', desc: 'Vos workflows s\'exécutent 24/7 sans intervention manuelle' },
    { feature: 'Scalabilité', desc: 'Départ petit, évolutif selon vos besoins. Augmentez la complexité facilement' }
  ];

  const useCases = [
    {
      category: 'E-commerce',
      examples: [
        'Synchronisation inventaire multi-canaux',
        'Génération descriptions produits par IA',
        'Emails marketing personnalisés IA',
        'Gestion commandes & listes de diffusion'
      ]
    },
    {
      category: 'Services Professionnels',
      examples: [
        'Traitement factures automatique',
        'Extraction données fromulaires',
        'Génération rapports IA',
        'Qualification leads chatbot IA'
      ]
    },
    {
      category: 'Associations & Collectivités',
      examples: [
        'Automatisation demandes administratives',
        'Transcription réunions IA',
        'Rapports activités générés IA',
        'Synchronisation données multi-systèmes'
      ]
    },
    {
      category: 'Support Client',
      examples: [
        'Chatbot IA tier-1 support',
        'Routage automatiquetickets',
        'Résumés conversations IA',
        'Classement/triage issues'
      ]
    }
  ];

  return (
    <div className="bg-[#181C25] text-white lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="pt-32 px-4 lg:px-8 relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/?scroll=ia-automation"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour aux services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-24 px-4 lg:px-8 bg-gradient-to-b from-[#212635] to-[#181C25]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center mb-20"
          >
            {/* Contenu */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-montserrat font-bold mb-6">
                IA & Automatisations <span className="text-purple-500">Intelligentes</span>
              </h1>
              <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                Transformez vos processus avec l'Intelligence Artificielle et les automatisations n8n. Gains de productivité et réduction de coûts garantis.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Solutions IA avancées (GPT-4, Claude, LLMs), workflows n8n sans code, intégrations intelligentes. Accompagnement complet du diagnostic à la formation. <strong className="text-gray-300">Services en visio ou sur site en Ardennes.</strong>
              </p>
              <a
                href="/?scroll=contact"
                className="inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-purple-600 hover:bg-purple-700 font-montserrat font-semibold transition-all"
              >
                <span>Demander une démo</span>
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Visuel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-96 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-2 border-purple-500/30 overflow-hidden flex items-center justify-center"
            >
              <img 
                src="https://images.unsplash.com/photo-1677442d019cecf8d4163fb58a28ed5f5170032a?w=500&h=500&fit=crop" 
                alt="IA et Automatisations" 
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#181C25] to-transparent flex items-end justify-center pb-6">
                <p className="text-gray-300 font-montserrat text-lg font-semibold">Solutions IA Avancées</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Phases IA */}
      <section className="py-24 px-4 lg:px-8">
        <div className="container mx-auto">
          <h2 className="text-4xl font-montserrat font-bold mb-16 text-white text-center">
            Notre Approche IA en 3 Phases
          </h2>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-3 gap-8 mb-24"
          >
            {aiFeaturesPhases.map((phase, idx) => {
              const Icon = phase.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-purple-500 transition-all"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 rounded-lg bg-purple-600 flex items-center justify-center mr-4">
                      <Icon size={28} />
                    </div>
                    <span className="text-3xl font-bold text-purple-400">{idx + 1}</span>
                  </div>
                  <h3 className="font-montserrat font-bold text-xl text-white mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-gray-300 mb-4">{phase.description}</p>
                  <ul className="space-y-2">
                    {phase.details.map((detail, didx) => (
                      <li key={didx} className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Check size={14} className="text-purple-400 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Fonctionnalités */}
          <h2 className="text-4xl font-montserrat font-bold mb-12 text-white text-center">
            Fonctionnalités Principales
          </h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24"
          >
            {automationFeatures.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="p-6 rounded-lg bg-gradient-to-r from-purple-600/10 to-blue-600/10 border border-purple-500/20"
              >
                <h3 className="font-montserrat font-bold text-white mb-2">{item.feature}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Cas d'usage */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white text-center">
              Cas d'Usage Réels
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {useCases.map((useCase, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-8 rounded-xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30"
                >
                  <h3 className="text-xl font-montserrat font-bold text-blue-400 mb-4">
                    {useCase.category}
                  </h3>
                  <ul className="space-y-3">
                    {useCase.examples.map((example, eidx) => (
                      <li key={eidx} className="flex items-start space-x-3 text-gray-300">
                        <ArrowRight size={16} className="mt-1 text-blue-400 flex-shrink-0" />
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Service en Ardennes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-24 p-12 rounded-2xl bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-green-400" size={28} />
                  <h3 className="text-2xl font-montserrat font-bold text-white">
                    Solutions IA & n8n en Ardennes
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  HTAG Facility accompagne PME, associations et collectivités en Ardennes dans leur transformation par l'IA et les automatisations. Basé à <strong>Château-Porcien</strong>, nous intervenons pour concevoir, implémenter et former vos équipes.
                </p>
                <p className="text-gray-400 text-sm">
                  <strong>Modes d'intervention :</strong> Ateliers de travail en visio, sur site, ou hybride selon vos préférences.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center p-6 rounded-xl bg-green-600/10 border border-green-500/30">
                  <p className="text-green-400 font-montserrat font-bold text-sm mb-2">ZONE INTERVENTION</p>
                  <p className="text-white font-bold text-lg">Ardennes</p>
                  <p className="text-gray-400 text-sm">Grand Est, France</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30"
          >
            <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
              Automatisez Votre Croissance avec IA & n8n
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez Eric pour une consultation gratuite. Découvrez comment optimiser vos processus, réduire les coûts et multiplier votre productivité avec IA et n8n.
            </p>
            <a
              href="/?scroll=contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:shadow-lg hover:shadow-purple-600/50 text-white font-montserrat font-semibold transition-all"
            >
              <span>Démarrer maintenant</span>
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default AIAutomationService;
